import { useQueryClient } from '@tanstack/react-query'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import ImportContactModal from 'components/ImportContactFlow/ImportContactModal'
import { ContactRequestsTable } from 'components/Tables/ContactRequestsTable/ContactRequestsTable'
import { ContactsTable } from 'components/Tables/ContactsTable/ContactsTable'
import useDocumentTitle from 'components/useDocumentTitle'
import React from 'react'
import { PiAddressBook, PiPlus, PiUploadSimple } from 'react-icons/pi'
import styled from 'styled-components/macro'
import { UnderlineTabs } from '../../../components/FbUI/UnderlineTabs'
import NewContactModal from '../../../components/Modals/NewContactModal'
import {
  CONTACT_REQUEST_JOB_TABLE_KEY,
  CONTACTS_TABLE_KEY,
} from '../../../constants/tableQueryKeys'
import { useNavigate, useParams } from 'react-router-dom'
import { useNavbarStore } from '../../../components/Layout/navbar-store'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { PATHS } from 'paths'
import {
  FBPageTableContainer,
  FBPageTitleRow,
} from '../../../components/FbUI/Page/styles'
import {
  BuyCreditsButton,
  CreditBalanceCard,
} from 'components/Credits/CreditBalanceCard'
import { toast } from 'react-toastify'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

export function ContactsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const params = useParams()
  const selectedTab = (params?.type ?? 'all') as 'all' | 'requests'
  const store = useNavbarStore()

  const [totalContacts, setTotalContacts] = React.useState<number | null>(null)
  const [showNewContactModal, setShowNewContactModal] = React.useState(false)
  const [showImportContactModal, setShowImportContactModal] =
    React.useState(false)
  // Lazy-mount-once: each tab mounts the first time it's visited and stays
  // mounted thereafter (hidden via display:none when inactive). Avoids the
  // loading→empty→filled flicker on every subsequent tab switch. Use a ref
  // so external URL changes (back/forward, deep link) — which bypass the
  // PillTabs onChange — still grow the set; the ref also doesn't trigger
  // re-renders on tab change since the panel visibility is driven by
  // selectedTab/style, not by mountedTabs identity.
  const mountedTabsRef = React.useRef<Set<'all' | 'requests'>>(
    new Set([selectedTab])
  )
  mountedTabsRef.current.add(selectedTab)
  const mountedTabs = mountedTabsRef.current

  const featureFlags = useFeatureFlagService()

  const handleUploadContactsClick = () => {
    if (featureFlags.enableContactImportWizard) {
      navigate(PATHS.CONTACTS_IMPORT_WIZARD)
    } else {
      setShowImportContactModal(true)
    }
  }

  const handleContactCreated = () => {
    queryClient.invalidateQueries({ queryKey: ['tags', 'contacts'] })
    queryClient.refetchQueries({ queryKey: [CONTACTS_TABLE_KEY] })
  }

  const handleContactUpdated = () => {
    queryClient.refetchQueries({ queryKey: [CONTACTS_TABLE_KEY] })
  }

  const selectedTabTitle =
    selectedTab === 'all' ? 'All Contacts' : 'Contact Requests'

  useDocumentTitle(
    `${selectedTab === 'all' ? 'All' : 'Requested'} Contacts - Contacts - First Bite`
  )

  // Check for success or canceled parameters in URL from Stripe redirect
  React.useEffect(() => {
    if (selectedTab === 'requests') {
      const urlParams = new URLSearchParams(window.location.search)
      const success = urlParams.get('success')
      const canceled = urlParams.get('canceled')

      console.log('Checking URL params:', {
        success,
        canceled,
        search: window.location.search,
      })

      if (success === 'true') {
        console.log('Credit purchase successful, showing success toast')
        toast.success('Credits purchased successfully!')

        // Clear the URL parameters
        if (window.history && window.history.replaceState) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          )
        }

        // If there was a localStorage callback marker, use it and remove it
        const hasCallback =
          localStorage.getItem('creditPurchaseSuccessCallback') === 'true'
        console.log('Credit purchase callback status:', { hasCallback })

        if (hasCallback) {
          localStorage.removeItem('creditPurchaseSuccessCallback')
          console.log('Refreshing credit information after purchase')
          // Refresh credit information
          queryClient.invalidateQueries({
            queryKey: ['creditBalance'],
          })
        }
      } else if (canceled === 'true') {
        console.log('Credit purchase canceled, showing info toast')
        toast.info('Credit purchase was canceled')

        // Clear the URL parameters
        if (window.history && window.history.replaceState) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          )
        }
      }
    }
  }, [selectedTab, queryClient])

  return (
    <FBPage innerPageClassName="!bg-[#fefefe]">
      <div
        className="flex w-full flex-row items-start justify-between gap-4"
        style={{ minHeight: 104 }}
      >
        <FBPageTitleRow>
          <div>
            <Eyebrow>
              <PiAddressBook size={16} />
              Contacts
            </Eyebrow>
            <Title>{selectedTabTitle}</Title>
          </div>

          {selectedTab === 'all' && (
            <div className={'flex flex-row gap-2.5 ml-auto'}>
              <Button
                className="!bg-brand-bright !text-white hover:!bg-brand-active"
                onClick={() => setShowNewContactModal(true)}
              >
                <PiPlus size={16} /> Add Contact
              </Button>
              <Button
                onClick={handleUploadContactsClick}
                variant="secondary"
                className="!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900"
              >
                <PiUploadSimple size={18} /> <span>Upload Contacts</span>
              </Button>
            </div>
          )}
        </FBPageTitleRow>

        {selectedTab === 'requests' && <CreditBalanceCard hideBuyButton />}

        {showImportContactModal && (
          <ImportContactModal
            show={showImportContactModal}
            handleClose={() => setShowImportContactModal(false)}
            onContactsImported={handleContactCreated}
          />
        )}

        {showNewContactModal && (
          <NewContactModal
            show={showNewContactModal}
            handleClose={() => setShowNewContactModal(false)}
            onContactCreated={handleContactCreated}
          />
        )}
      </div>

      <div className="flex items-end justify-between gap-4 mb-3">
        <UnderlineTabs<'all' | 'requests'>
          tabs={[
            {
              key: 'all',
              title: 'All Contacts',
              count: totalContacts,
            },
            {
              key: 'requests',
              title: 'Contact Requests',
            },
          ]}
          activeKey={selectedTab}
          onChange={(value) => {
            const url = `/contacts/${value}`
            navigate(url)
            store.setLastContactVisited(url)
          }}
          idPrefix="contacts"
          aria-label="Contacts type"
        />
        {selectedTab === 'requests' && <BuyCreditsButton />}
      </div>

      <TableLinkStyleProvider variant="plain">
        <>
          {mountedTabs.has('all') && (
            <FBPageTableContainer
              className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
              style={{
                display: selectedTab === 'all' ? 'flex' : 'none',
              }}
            >
              <ContactsTable
                defaultColumnVisibility={{
                  requested_contact__campaign__name: false,
                }}
                tableKey={CONTACTS_TABLE_KEY}
                setTotalRowsCount={setTotalContacts}
                handleContactUpdated={handleContactUpdated}
              />
            </FBPageTableContainer>
          )}

          {mountedTabs.has('requests') && (
            <FBPageTableContainer
              className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
              style={{
                display: selectedTab === 'requests' ? 'flex' : 'none',
              }}
            >
              <ContactRequestsTable tableKey={CONTACT_REQUEST_JOB_TABLE_KEY} />
            </FBPageTableContainer>
          )}
        </>
      </TableLinkStyleProvider>
    </FBPage>
  )
}

const Button = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  background: #f3f4f6;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.2px;
  padding: 2px 10px;
  border-radius: 9999px;
  margin-bottom: 8px;
`

const Title = styled.h1`
  font-size: 24px;
  line-height: 28px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
`
